// DRAFT WITH THE AGENT — the form modals' fold-out chat. The SHELL is the shared flap-out
// (components/AgentFlyout.tsx — the same spine, thread, activity band and composer the
// workbench uses); this file owns only what makes it a FORM chat: the preamble that carries
// the live field values every turn, the field-tagged proposal blocks (<<<FIELD key>>> … <<<END>>>)
// parsed out of each reply, and the take buttons that fill the form. P1 exactly as everywhere
// else: ◇ said by the machine, committed only by a person — the take fills a form the operator
// still submits (or a blur-save field they still own), never a record directly.
//
// It rides the ordinary conversation machinery (POST /api/agent/conversation): the agent has
// the whole corpus and register behind it, which is the point — "draft a DoD" can read what the
// requirements actually say. The preamble forbids propose_* tools here: the FORM is the
// destination, not the queue.
import { useCallback, useEffect, useMemo, useState } from 'react';

import { agentApi, type TurnActivity } from '../api/agent.js';
import AgentFlyout, { type FlyoutMsg } from './AgentFlyout.js';
import { useT } from '../i18n/index.js';
import './FieldChat.de.js';
import './FieldChat.fr.js';

export interface ChatField {
  /** The key the agent tags its block with — short snake_case, e.g. definition_of_done. */
  key: string;
  /** The human name on the take button and the block head. */
  label: string;
  get: () => string;
  set: (text: string) => void;
}

interface FieldBlock { key: string; text: string }
interface Msg { who: 'op' | 'agent'; text: string; blocks?: FieldBlock[] }

const BLOCK_RE = /<<<FIELD ([a-z_]+)>>>\s*([\s\S]*?)\s*<<<END>>>/g;

export function FieldChat({ engagementId, title, contextLines, fields }: {
  engagementId: number;
  /** Names the form in the preamble — "New deliverable", "DEL-04", "Revise REQ-012". */
  title: string;
  /** What the host form knows beyond its fields — satisfies refs, classification, … */
  contextLines?: string[];
  fields: ChatField[];
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [runId, setRunId] = useState<number | null>(null);
  const [ask, setAsk] = useState('');
  const [busy, setBusy] = useState(false);
  /** The live doing behind the blocking turn — clock-only on a thread's very first turn. */
  const [turnAct, setTurnAct] = useState<TurnActivity | null>(null);
  const [askStartedAt, setAskStartedAt] = useState(0);
  useEffect(() => {
    if (!busy || runId === null) { setTurnAct(null); return; }
    let live = true;
    const poll = async () => {
      try {
        const a = await agentApi.runActivity(runId);
        if (live) setTurnAct(a.busy ? a : null);
      } catch { /* transient — next poll answers */ }
    };
    void poll();
    const iv = setInterval(() => void poll(), 2000);
    return () => { live = false; clearInterval(iv); };
  }, [busy, runId]);

  /** The live form travels with EVERY turn — steering only works if the agent sees the wheel. */
  const valueLines = () =>
    fields.map((f) => {
      const v = f.get().trim().replace(/\s+/g, ' ');
      return `- ${f.key}: ${v ? v.slice(0, 500) : '(empty)'}`;
    });

  const send = useCallback(async () => {
    const q = ask.trim();
    if (!q || busy) return;
    setBusy(true);
    setAskStartedAt(Date.now());
    setMsgs((m) => [...m, { who: 'op', text: q }]);
    setAsk('');
    const message = (runId === null
      ? [
          `[Form drafting — ${title}]`,
          'You are helping the operator fill a FORM, live. Nothing you write enters any record: the operator takes a proposal into a field and commits it themselves. Do NOT use any propose_* tool in this conversation — the form, not the queue, is the destination.',
          ...(contextLines ?? []),
          'Current field values:',
          ...valueLines(),
          'When you propose text for a field, output it EXACTLY as (the text alone between the markers):',
          '<<<FIELD field_key>>>',
          'the proposed text',
          '<<<END>>>',
          `Field keys: ${fields.map((f) => f.key).join(', ')}. At most one block per field per answer; outside the blocks, talk normally. Write field text in the engagement's correspondence language.`,
          '',
          `Operator: ${q}`,
        ]
      : ['[Field values now:]', ...valueLines(), '', `Operator: ${q}`]
    ).join('\n');
    try {
      const out = await agentApi.conversation(engagementId, message, runId);
      const blocks: FieldBlock[] = [];
      const text = out.reply
        .replace(BLOCK_RE, (whole, key: string, body: string) => {
          if (fields.some((f) => f.key === key)) {
            blocks.push({ key, text: body.trim() });
            return '';
          }
          return whole;
        })
        .trim();
      setMsgs((m) => [...m, { who: 'agent', text, blocks }]);
      setRunId(Number(out.run.id));
    } catch (err) {
      setMsgs((m) => [...m, { who: 'agent', text: err instanceof Error ? err.message : t('The agent did not answer.') }]);
    } finally {
      setBusy(false);
    }
  }, [ask, busy, runId, engagementId, title, contextLines, fields, t]);

  /** The thread in the shared flyout's shape — take-into-field blocks ride as extras. */
  const flyoutMsgs = useMemo<FlyoutMsg[]>(() => msgs.map((m) => ({
    who: m.who,
    text: m.text,
    extras: m.who === 'agent' && m.blocks?.length ? (
      <>
        {m.blocks.map((b, j) => {
          const field = fields.find((f) => f.key === b.key);
          if (!field) return null;
          return (
            <div className="fieldchat-block" key={j}>
              <div className="bh">
                <span className="att att--proposed" aria-hidden="true" />
                <span className="tag-unheld">{field.label}</span>
              </div>
              <div className="bt">{b.text}</div>
              <button type="button" className="btn btn-sm btn-accent" onClick={() => field.set(b.text)}>
                {t('Take into {field}', { field: field.label })}
              </button>
            </div>
          );
        })}
      </>
    ) : undefined,
  })), [msgs, fields, t]);

  return (
    <AgentFlyout
      open={open}
      onFold={setOpen}
      label={t('Draft with the agent')}
      byline={t('agent · proposes into the fields, a take fills the form')}
      msgs={flyoutMsgs}
      busy={busy}
      activity={turnAct}
      busyStartedAt={askStartedAt}
      ask={ask}
      onAskChange={setAsk}
      onSend={() => void send()}
      placeholder={t('e.g. “Draft a checkable definition of done”')}
      emptyState={
        <p className="rw-agent-note">
          {t('Discuss what belongs in the fields. The agent reads the engagement, proposes text per field, and you steer — a proposal lands only when you take it.')}
        </p>
      }
    />
  );
}

export default FieldChat;
